import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  type Signal,
} from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import type { EChartsOption } from 'echarts';
import { buildEchartsTheme } from '../echarts-theme';

export interface IEchartsDonutDatum {
  name: string;
  value: number;
  colour?: string;
}

@Component({
  selector: 'pr-echarts-donut',
  templateUrl: './echarts-donut.component.html',
  styleUrls: ['./echarts-donut.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxEchartsDirective],
})
export class EchartsDonutComponent {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly data = input<IEchartsDonutDatum[]>([]);
  readonly label = input.required<string>();
  readonly centreText = input<string>();
  readonly height = input<number>(280);
  readonly ariaLabelOverride = input<string>();

  readonly hasData = computed(() => this.data().length > 0);

  readonly ariaLabel = computed(() => {
    const override = this.ariaLabelOverride();
    if (override) {
      return override;
    }
    const slices = this.data();
    if (slices.length === 0) {
      return this.label();
    }
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);
    const summary = slices
      .map((slice) => `${slice.name}: ${slice.value}`)
      .join(', ');
    return `${this.label()} donut chart, total ${total}, ${summary}`;
  });

  readonly chartOptions: Signal<EChartsOption> = computed(() => {
    const slices = this.data();
    if (slices.length === 0) {
      return {};
    }

    const theme = buildEchartsTheme(this._elementRef.nativeElement);
    const centre = this.centreText();

    const seriesData = slices.map((slice) => ({
      name: slice.name,
      value: slice.value,
      ...(slice.colour ? { itemStyle: { color: slice.colour } } : {}),
    }));

    const graphic: EChartsOption['graphic'] = centre
      ? [
          {
            type: 'text',
            left: 'center',
            top: 'center',
            style: {
              text: centre,
              fontSize: 18,
              fontWeight: 600,
              fill: theme.textStyle?.color ?? '',
            },
          },
        ]
      : [];

    return {
      ...theme,
      tooltip: {
        ...theme.tooltip,
        trigger: 'item',
      },
      legend: {
        ...theme.legend,
        orient: 'vertical',
        right: 16,
        top: 'center',
      },
      graphic,
      series: [
        {
          type: 'pie',
          name: this.label(),
          radius: ['60%', '85%'],
          avoidLabelOverlap: true,
          label: {
            show: false,
          },
          emphasis: {
            label: {
              show: true,
              formatter: '{b}\n{d}%',
              fontSize: 12,
            },
          },
          data: seriesData,
        },
      ],
    };
  });
}
